import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionSummary, Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../../../../../../../hooks/useStores";
import useImageUtils from "../../../../../../../utils/useImageUtils.tsx";
import TextToImageAspectRatio from "../../../../../../Playground/TextToImage/Config/Advanced/AspectRatio/TextToImageAspectRatio";
import TextToImageSeed from "../../../../../../Playground/TextToImage/Config/Advanced/Seed/TextToImageSeed";
import TextToImageGuidance from "../../../../../../Playground/TextToImage/Config/Advanced/TextGuidance/TextToImageGuidance";
import TextToImageControlNet from "../../../../../../Playground/TextToImage/Config/Advanced/TextToImageControlNet.tsx/TextToImageControlNet";
import TextToImageEnhanceSwitch from "../../../../../../Playground/TextToImage/Config/Advanced/TextToImageEnhanceSwitch.tsx/TextToImageEnhanceSwitch";
import TextToImageNegativePrompt from "../../../../../../Playground/TextToImage/Config/Advanced/TextToImageNegativePrompt/TextToImageNegativePrompt";
import TgFastSwitch from "../../../../../App/Image/Config/Fast/TgFastSwitch";
import { shouldHideControlNets, shouldHideFastToggle } from "../../../../../utils/tg-playground-helpers.ts";
import styles from "../TgEvaluationConfig.module.scss";

interface TgConfigAdvancedProps {
	scrollContainer: React.RefObject<HTMLElement> | HTMLElement | null;
}

const TgConfigAdvanced = ({ scrollContainer }: TgConfigAdvancedProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.viewer.evaluation.config" });
	const accordionRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const { textToImageStore } = useAppStore();
	const { getAspectRatioFromBase64 } = useImageUtils();
	const [controlNetError, setControlNetError] = useState(false);
	const [cn1AspectRatio, setCn1AspectRatio] = useState<string | undefined>(undefined);
	const [cn2AspectRatio, setCn2AspectRatio] = useState<string | undefined>(undefined);

	const validateAspectRatios = async (changed: 1 | 2, base64?: string) => {
		try {
			if (!base64) {
				if (changed === 1) {
					// CN1 removed/toggled off: clear source-of-truth and any CN2 error
					setCn1AspectRatio(undefined);
					setControlNetError(false);
					textToImageStore.controlNetAspectRatioError = false;
					return;
				}
				if (changed === 2) {
					// CN2 removed/toggled off: clear its ratio and error
					setCn2AspectRatio(undefined);
					setControlNetError(false);
					textToImageStore.controlNetAspectRatioError = false;
					return;
				}
			}

			const numericRatio = (await getAspectRatioFromBase64(base64!)) as number;
			const simplified = (() => {
				const precision = 1e-6;
				let bestNum = 1,
					bestDen = 1,
					bestError = Math.abs(numericRatio - bestNum / bestDen);
				for (let den = 1; den <= 100; den++) {
					const num = Math.round(numericRatio * den);
					const err = Math.abs(numericRatio - num / den);
					if (err + precision < bestError) {
						bestError = err;
						bestNum = num;
						bestDen = den;
					}
				}
				const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
				const d = gcd(bestNum, bestDen);
				return `${Math.round(bestNum / d)}:${Math.round(bestDen / d)}`;
			})();

			if (changed === 1) {
				// Update source-of-truth ratio
				setCn1AspectRatio(simplified);
				// If CN2 exists, validate it against CN1
				if (cn2AspectRatio) {
					const equal = cn2AspectRatio === simplified;
					setControlNetError(!equal);
					textToImageStore.controlNetAspectRatioError = !equal;
				} else {
					setControlNetError(false);
					textToImageStore.controlNetAspectRatioError = false;
				}
				return;
			}

			if (changed === 2) {
				setCn2AspectRatio(simplified);
				// Only CN2 can be in error, and only if CN1 exists
				if (cn1AspectRatio) {
					const equal = simplified === cn1AspectRatio;
					setControlNetError(!equal);
					textToImageStore.controlNetAspectRatioError = !equal;
				} else {
					setControlNetError(false);
					textToImageStore.controlNetAspectRatioError = false;
				}
				return;
			}
		} catch {
			setControlNetError(false);
			textToImageStore.controlNetAspectRatioError = false;
		}
	};

	return (
		<Accordion
			ref={accordionRef}
			elevation={0}
			disableGutters
			TransitionProps={{ timeout: 200 }}
			className={styles.advancedAccordion}
		>
			<AccordionSummary expandIcon={<ExpandMore />}>{t("label")}</AccordionSummary>
			<Box ref={contentRef} className={styles.advanced}>
				<TextToImageAspectRatio />
				{!shouldHideFastToggle(window.location.pathname) && <TgFastSwitch />}
				<TextToImageSeed hideDisplayToggle />
				<TextToImageGuidance />
				<TextToImageNegativePrompt />
				<TextToImageEnhanceSwitch />
				{!shouldHideControlNets(window.location.pathname) && (
					<>
						<TextToImageControlNet
							controlNetNumber={1}
							defaultEnabled
							scrollContainer={scrollContainer}
							onImageUploaded={async (base64?: string) => validateAspectRatios(1, base64)}
						/>
						<TextToImageControlNet
							controlNetNumber={2}
							scrollContainer={scrollContainer}
							onImageUploaded={async (base64?: string) => validateAspectRatios(2, base64)}
							hasError={controlNetError}
						/>
					</>
				)}
			</Box>
		</Accordion>
	);
};

export default observer(TgConfigAdvanced);
